import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import { revokeToken } from '../../util/api';
// import { showUser } from '../ProfilePage/ProfilePage.duck';

const { LatLng, UUID } = sdkTypes;

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

export const UNLINK_CALENDAR_REQUEST = 'app/ProfileSettingsPage/UNLINK_CALENDAR_REQUEST';
export const UNLINK_CALENDAR_SUCCESS = 'app/ProfileSettingsPage/UNLINK_CALENDAR_SUCCESS';
export const UNLINK_CALENDAR_ERROR = 'app/ProfileSettingsPage/UNLINK_CALENDAR_ERROR';

export const CREATE_LISTING_REQUEST = 'app/ProfileSettingsPage/CREATE_LISTING_REQUEST';
export const CREATE_LISTING_SUCCESS = 'app/ProfileSettingsPage/CREATE_LISTING_SUCCESS';
export const CREATE_LISTING_ERROR = 'app/ProfileSettingsPage/CREATE_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
  unlinkCalendarInProgress: false,
  unlinkCalendarError: null,
  createListingInProgress: false,
  createListingError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CREATE_LISTING_REQUEST:
      return {
        ...state,
        createListingInProgress: true,
      };
    case CREATE_LISTING_SUCCESS:
      return {
        ...state,
        createListingInProgress: false,
      };
    case CREATE_LISTING_ERROR:
      return {
        ...state,
        createListingError: payload,
      };
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case UNLINK_CALENDAR_REQUEST:
      return {
        ...state,
        unlinkCalendarInProgress: true,
        unlinkCalendarError: null,
      };
    case UNLINK_CALENDAR_SUCCESS:
      return {
        ...state,
        unlinkCalendarInProgress: false,
      };
    case UNLINK_CALENDAR_ERROR:
      return {
        ...state,
        unlinkCalendarInProgress: false,
        unlinkCalendarError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

export const unlinkCalendarRequest = () => ({
  type: UNLINK_CALENDAR_REQUEST,
});
export const unlinkCalendarSuccess = () => ({
  type: UNLINK_CALENDAR_SUCCESS,
});
export const unlinkCalendarError = error => ({
  type: UNLINK_CALENDAR_ERROR,
  payload: error,
  error: true,
});

export const createListingRequest = params => ({
  type: CREATE_LISTING_REQUEST,
  payload: { params },
});
export const createListingSuccess = result => ({
  type: CREATE_LISTING_SUCCESS,
  payload: result.data,
});
export const createListingError = error => ({
  type: CREATE_LISTING_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

export const createListing = listingData => (dispatch, getState, sdk) => {
  dispatch(createListingRequest());

  // Destructuring safely with defaults
  const attributes = listingData?.data?.data?.attributes || {};
  const profile = attributes.profile || {};
  const profilePublicData = profile?.publicData || {};
  const included = listingData?.data?.included || [];

  // Generating image IDs
  const imageIds = included.map(data => new UUID(data?.id?.uuid || ''));

  const data = {
    title: profile?.displayName || 'Default Title',
    description: `${profile?.firstName} ${profile?.lastName} ${attributes?.email}`,
    geolocation: new LatLng(
      profilePublicData?.location?.origin?.lat,
      profilePublicData?.location?.origin?.lng
    ),
    publicData: {
      bio: profile?.bio || '',
      languages: profilePublicData?.languages || [],
      location: profilePublicData?.location || '',
      sacredPractice: profilePublicData?.sacredPractice || '',
      listingType: 'profileListing',
      wisdomAndWays: profilePublicData?.wisdomAndWays || '',
    },
    // images: imageIds, // Use imageIds if necessary
  };

  // First promise: Creating the listing
  sdk.ownListings
    .create(data)
    .then(res => {
      console.log('Listing created successfully:', res);

      // Prepare data for the second promise
      const updatedProfilePublicData = {
        publicData: {
          profileListingId: res.data.data.id.uuid,
        },
      };
      const queryParams = {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      };

      // Second promise: Update profile with new listing ID
      return sdk.currentUser
        .updateProfile(updatedProfilePublicData, queryParams)
        .then(updateRes => {
          console.log('Profile updated successfully:', updateRes);
        })
        .catch(updateError => {
          console.error('Error updating profile:', updateError);
          dispatch(createListingError(updateError)); // Handle profile update errors separately
        });
    })
    .catch(createError => {
      console.error('Error creating listing:', createError);
      dispatch(createListingError(createError)); // Handle listing creation errors separately
    });
};

export const updateListing = (listingData, profileListingId) => (dispatch, getState, sdk) => {
  dispatch(createListingRequest());

  const { attributes } = listingData.data.data;
  const { profile } = attributes;
  const { publicData: profilePublicData } = profile;
  const { included } = listingData?.data;
  const imageIds = included?.map(data => {
    return new UUID(data?.id?.uuid);
  });

  const data = {
    id: new UUID(profileListingId),
    title: profile?.displayName,
    description: `${profile?.firstName} ${profile?.lastName} ${attributes?.email}`,
    geolocation: new LatLng(
      profilePublicData?.location?.origin?.lat,
      profilePublicData?.location?.origin?.lng
    ),
    publicData: {
      bio: profile?.bio,
      languages: profilePublicData?.languages,
      location: profilePublicData?.location,
      sacredPractice: profilePublicData?.sacredPractice,
      listingType: 'profileListing',
      wisdomAndWays: profilePublicData?.wisdomAndWays,
    },
    // images: [new UUID(included[0].id.uuid)],
  };

  return sdk.ownListings.update(data);
};

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const unlinkCalendar = params => (dispatch, getState, sdk) => {
  dispatch(unlinkCalendarRequest());

  return revokeToken(params)
    .then(res => {
      if (res.success) {
        dispatch(fetchCurrentUser());
        dispatch(unlinkCalendarSuccess());
        window.location.reload();
      } else {
        dispatch(unlinkCalendarError('Unlinking Failed'));
      }
    })
    .catch(e => {
      dispatch(unlinkCalendarError('Unlinking Failed'));
    });
};

export const updateProfile = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
        dispatch(fetchCurrentUser());
        // dispatch(showUser());
        return response;
      })
      .then(res => {
        const { attributes } = res.data.data;
        const { profile } = attributes;
        const { publicData } = profile;
        const { profileListingId } = publicData;
        if (profileListingId) {
          dispatch(updateListing(res, profileListingId));
        } else {
          dispatch(createListing(res));
        }
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
};
